/**
 * @fileoverview World Event V3 schema definitions for historical events, prophecies, and legends.
 *
 * This file contains:
 * - Canonical field definitions for world events (single source of truth)
 * - Dynamically generated Zod schemas with type-safe casting
 * - All event-related schemas (entity, snapshot, idea, context variants)
 *
 * Pattern follows the character domain (character-sub-entities-v2.ts) and world-place.ts.
 */

import { z } from 'zod';
import { SHARED_WORLD_ELEMENT_FIELDS, type WorldElementFieldDefinition } from './shared-fields';

// =============================================================================
// CANONICAL FIELD DEFINITIONS - SINGLE SOURCE OF TRUTH
// =============================================================================

/**
 * Event-specific field definitions.
 * Combined with shared fields, these define all attributes of a world event.
 */
const EVENT_SPECIFIC_FIELDS: WorldElementFieldDefinition[] = [
  {
    key: 'date',
    label: 'Date & Timeline',
    schemaDescription: "When it occurred relative to the story",
    initialStateDescription: "When this event occurred or is expected to occur relative to the story's timeline.",
    journeyDescription: "Updated timeline information or newly revealed dates and temporal context.",
  },
  {
    key: 'location',
    label: 'Location',
    schemaDescription: "Where it took place",
    initialStateDescription: "Where this event took place or is foretold to occur.",
    journeyDescription: "Current understanding of where the event occurred and any newly revealed locations.",
  },
  {
    key: 'participants',
    label: 'Key Participants',
    schemaDescription: "Who was involved",
    initialStateDescription: "Who was involved in this event at the start of the story.",
    journeyDescription: "Known participants and any newly revealed individuals or groups involved.",
  },
  {
    key: 'causes',
    label: 'Causes & Triggers',
    schemaDescription: "What led to this event",
    initialStateDescription: "What led to this event or what conditions are required for it to occur.",
    journeyDescription: "Current understanding of causes and any newly revealed triggers or preconditions.",
  },
  {
    key: 'consequences',
    label: 'Consequences & Impact',
    schemaDescription: "What changed because of this event",
    initialStateDescription: "What changed because of this event or what is expected to change.",
    journeyDescription: "Known consequences and any newly revealed or ongoing impacts.",
  },
  {
    key: 'duration',
    label: 'Duration & Scope',
    schemaDescription: "How long it lasted and how widespread",
    initialStateDescription: "How long the event lasted and how widespread its effects were.",
    journeyDescription: "Current understanding of the event's duration and scope of impact.",
  },
  {
    key: 'evidence',
    label: 'Evidence & Memory',
    schemaDescription: "What remains or how it's remembered",
    initialStateDescription: "What physical evidence remains and how this event is remembered.",
    journeyDescription: "Known evidence, monuments, or traditions and any newly discovered remnants.",
  },
  {
    key: 'disputed',
    label: 'Disputed Facts',
    schemaDescription: "Conflicting accounts or mysteries",
    initialStateDescription: "Conflicting accounts, unresolved mysteries, or disputed aspects of this event.",
    journeyDescription: "Current disputes and any newly revealed contradictions or clarifications.",
  },
];

/**
 * Complete field definitions for world events.
 * Combines shared fields with event-specific fields.
 */
export const WORLD_EVENT_FIELD_DEFINITIONS: readonly WorldElementFieldDefinition[] = [
  ...SHARED_WORLD_ELEMENT_FIELDS,
  ...EVENT_SPECIFIC_FIELDS,
] as const;

// =============================================================================
// ENUMS
// =============================================================================

/**
 * Element subtypes for world events.
 * - historical-event: Past occurrences that shaped the world (The Great Cataclysm, The Founding of the Republic)
 * - prophecy: Foretold future events ("The Chosen One shall rise when the stars align")
 * - legend: Mythical or semi-historical stories (The Tale of the First King, The Myth of Creation)
 */
export const WorldEventElementEnum = z.enum(['historical-event', 'prophecy', 'legend']);
export type WorldEventElement = z.infer<typeof WorldEventElementEnum>;

// =============================================================================
// DYNAMICALLY GENERATED SCHEMAS (following character domain pattern)
// =============================================================================

/**
 * Builds the base world event attributes schema from the canonical field definitions.
 * All fields are optional for world events.
 */
const buildAttributesSchema = () => {
  const schemaShape = WORLD_EVENT_FIELD_DEFINITIONS.reduce((acc, field) => {
    acc[field.key] = z.string().optional().describe(field.schemaDescription);
    return acc;
  }, {} as Record<string, z.ZodOptional<z.ZodString>>);

  // Type cast to preserve TypeScript inference (must be kept in sync with field definitions)
  return z.object(schemaShape as {
    // Shared fields
    relevanceToPlot: z.ZodOptional<z.ZodString>;
    currentState: z.ZodOptional<z.ZodString>;
    knownInformation: z.ZodOptional<z.ZodString>;
    // Event-specific fields
    date: z.ZodOptional<z.ZodString>;
    location: z.ZodOptional<z.ZodString>;
    participants: z.ZodOptional<z.ZodString>;
    causes: z.ZodOptional<z.ZodString>;
    consequences: z.ZodOptional<z.ZodString>;
    duration: z.ZodOptional<z.ZodString>;
    evidence: z.ZodOptional<z.ZodString>;
    disputed: z.ZodOptional<z.ZodString>;
  });
};

/**
 * The base schema for world event attributes, used by agents that generate or extract
 * event details. All attributes are optional.
 */
export const WorldEventAttributesV3Schema = buildAttributesSchema();

// =============================================================================
// ENTITY SCHEMAS
// =============================================================================

/**
 * A point-in-time snapshot of a world event's state at the end of a specific chapter.
 * Extends attributes with temporal metadata.
 */
export const WorldEventSnapshotV3Schema = WorldEventAttributesV3Schema.extend({
  id: z.string().describe("Unique identifier for this snapshot (typically matches chapterId)."),
  chapterId: z.string().describe("ID of the chapter this snapshot corresponds to."),
  chapterNumber: z.number().describe("The number of the chapter."),
  timestamp: z.string().describe("ISO timestamp of when this snapshot was created."),
  notPresentInChapter: z.boolean().optional().describe("Flag indicating this is a carry-forward snapshot."),
});

/**
 * The canonical World Event entity.
 * A container for metadata and a timeline of snapshots.
 */
export const WorldEventV3Schema = z.object({
  id: z.string().describe("Unique identifier for the world event."),
  name: z.string().describe("The name of the event."),
  type: z.literal('event').describe("Discriminator for world element type."),
  element: WorldEventElementEnum.describe("The subtype of event (historical-event, prophecy, or legend)."),
  initialDescription: z.string().optional().describe("Brief description of the event before detailed generation."),
  snapshots: z.array(WorldEventSnapshotV3Schema).describe("Complete timeline of event state across chapters."),
  latestSnapshotId: z.string().optional().describe("Quick reference to the most recent snapshot."),
  notes: z.string().optional().describe("User notes about this event."),
  mainImageId: z.string().optional().describe("Reference to the main image for this event."),
});

// =============================================================================
// IDEATION SCHEMAS
// =============================================================================

/**
 * Schema for a world event idea before it's added to the project.
 * Used during AI ideation and extraction flows.
 */
export const WorldEventIdeaV3Schema = z.object({
  tempId: z.string().describe("Temporary ID for UI tracking before persistence."),
  name: z.string().describe("The proposed name of the event."),
  element: WorldEventElementEnum.describe("The proposed subtype (historical-event, prophecy, or legend)."),
  initialDescription: z.string().describe("Brief description of the event concept."),
  source: z.enum(['ideation', 'extraction', 'manual']).describe("How this idea was generated."),
});

// =============================================================================
// CONTEXT SCHEMAS (for AI agents)
// =============================================================================

/**
 * Minimal reference schema for world events.
 * Used when only ID and name are needed.
 */
export const WorldEventRefV3Schema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('event'),
});

/**
 * Basic context schema for world events.
 * Used for high-level summaries in agent context.
 */
export const WorldEventBasicV3Schema = z.object({
  name: z.string(),
  type: z.literal('event'),
  element: WorldEventElementEnum,
  initialDescription: z.string().optional(),
});

/**
 * Rich context schema for world events.
 * Includes the full entity plus the resolved latest snapshot.
 */
export const WorldEventContextV3Schema = WorldEventV3Schema.extend({
  latestSnapshot: WorldEventSnapshotV3Schema.optional(),
});

// =============================================================================
// TYPE INFERENCE (single source of truth from Zod schemas)
// =============================================================================

export type WorldEventAttributes = z.infer<typeof WorldEventAttributesV3Schema>;
export type WorldEventSnapshotV3 = z.infer<typeof WorldEventSnapshotV3Schema>;
export type WorldEventV3 = z.infer<typeof WorldEventV3Schema>;
export type WorldEventIdeaV3 = z.infer<typeof WorldEventIdeaV3Schema>;
export type WorldEventRefV3 = z.infer<typeof WorldEventRefV3Schema>;
export type WorldEventBasicV3 = z.infer<typeof WorldEventBasicV3Schema>;
export type WorldEventContextV3 = z.infer<typeof WorldEventContextV3Schema>;
